import { defineStore } from "pinia"
import { ref } from "vue"

export type NotificationType = "success" | "error" | "warning" | "info"

export interface NotificationConfig {
  type: NotificationType
  message: string
  timeout?: number
  persistent?: boolean
  actionText?: string
  actionCallback?: () => void
}

export const useNotificationStore = defineStore("notificationStore", () => {
  const snackbar = ref(false)
  const message = ref("")
  const type = ref<NotificationType>("success")
  const timeout = ref(5000)
  const persistent = ref(false)
  const actionText = ref("")
  const actionCallback = ref<(() => void) | null>(null)

  // Color mapping with WCAG compliant colors
  const getNotificationColors = (notificationType: NotificationType) => {
    const colorMap = {
      success: {
        background: "#1B5E20", // Dark Green 900
        text: "#FFFFFF",
        border: "#2E7D32",     // Green 800
        icon: "mdi-check-circle"
      },
      error: {
        background: "#B71C1C", // Red 900
        text: "#FFFFFF", 
        border: "#D32F2F",     // Red 700
        icon: "mdi-alert-circle"
      },
      warning: {
        background: "#E65100", // Orange 900
        text: "#FFFFFF",
        border: "#F57C00",     // Orange 700
        icon: "mdi-alert"
      },
      info: {
        background: "#0D47A1", // Blue 900
        text: "#FFFFFF",
        border: "#1976D2",     // Blue 700
        icon: "mdi-information"
      }
    }
    return colorMap[notificationType]
  }

  const showNotification = (config: NotificationConfig) => {
    type.value = config.type
    message.value = config.message
    timeout.value = config.timeout ?? (config.type === "error" ? -1 : 5000)
    persistent.value = config.persistent ?? false
    actionText.value = config.actionText ?? ""
    actionCallback.value = config.actionCallback ?? null
    snackbar.value = true
  }

  const showSuccess = (message: string, timeout?: number) => {
    showNotification({ type: "success", message, timeout })
  }

  const showError = (message: string, persistent = true) => {
    showNotification({ 
      type: "error", 
      message, 
      timeout: persistent ? -1 : 8000,
      persistent 
    })
  }

  const showWarning = (message: string, timeout = 7000) => {
    showNotification({ type: "warning", message, timeout })
  }

  const showInfo = (message: string, timeout = 6000) => {
    showNotification({ type: "info", message, timeout })
  }

  const showWithAction = (
    type: NotificationType, 
    message: string, 
    actionText: string, 
    actionCallback: () => void
  ) => {
    showNotification({ 
      type, 
      message, 
      actionText, 
      actionCallback,
      timeout: -1, // Keep open until action is taken
      persistent: true
    })
  }

  const executeAction = () => {
    if (actionCallback.value) {
      actionCallback.value()
      close()
    }
  }

  const close = () => {
    snackbar.value = false
    // Reset state
    setTimeout(() => {
      actionText.value = ""
      actionCallback.value = null
      persistent.value = false
    }, 300)
  }

  return {
    snackbar,
    message,
    type,
    timeout,
    persistent,
    actionText,
    actionCallback,
    getNotificationColors,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showWithAction,
    executeAction,
    close
  }
})
